import { createColumnHelper } from '@tanstack/react-table';
import { Dispatch, MouseEvent, SetStateAction, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, IconSvg, Switch, Text, Tooltip } from '@/lib/v2/components';
import { ClockIcon } from '@/lib/v2/icons/solid';

import { useStateUserContext } from '@/src/application/context/UserContext';
import { IntegrationItem } from '@/src/infrastructure/interfaces/IIntegrations.interface';
import { useIntegrationService } from '@/src/infrastructure/Protocol/Integrations/useIntegrationService';
import { ActionRowMenu } from '@/src/modules/AutomationModuleNew/components';
import { EJourneyMessageState } from '@/src/modules/AutomationModuleNew/interfaces/automation';
import { INTEGRATIONS_PATHS } from '@/src/modules/IntegrationsModule/constants';
import { IntegrationTableIcon } from '@/src/modules/IntegrationsModule/icons/IntegrationTableIcon';
import { formatServeStringDateWithUserTimezone } from '@/src/utils/Date/dateUtils';

import { useHandleRowActions } from '@/modules/AutomationModuleNew/hooks/useHandleRowActions';

export const useIntegrationsTableColumns = (
  setDataIntegrationTable?: Dispatch<SetStateAction<IntegrationItem[]>>
) => {
  const { t } = useTranslation();
  const integrationService = useIntegrationService();
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<IntegrationItem>();
  const { data: actionRowMenuData, handleRowMenuClick } = useHandleRowActions<IntegrationItem>();
  const { userData } = useStateUserContext();

  const handleGoDetail = useCallback(
    (event: MouseEvent<HTMLButtonElement>, rowData: IntegrationItem) => {
      event.stopPropagation();
      const { journeyId } = rowData;
      navigate(`${journeyId}/${INTEGRATIONS_PATHS.REPORT}`);
    },
    [navigate]
  );

  const handleSwitchChange = useCallback(
    (id: number) => async (isChecked: boolean) => {
      const response = await integrationService.updateIntegrationStatus(id, isChecked);
      if (response.success) {
        setDataIntegrationTable?.((prevData) => {
          return prevData.map((item) => {
            if (Number(item.journeyId) === Number(id)) {
              return {
                ...item,
                status: isChecked ? EJourneyMessageState.ACTIVE : EJourneyMessageState.INACTIVE,
              };
            }
            return item;
          });
        });
      }
    },
    [integrationService, setDataIntegrationTable]
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: t('useRulesTableColumns.headerTable.name') as unknown as string,
        cell: (info) => {
          const isActive = info.row.original.status === EJourneyMessageState.ACTIVE;
          return (
            <div className="flex w-full flex-row items-center gap-4">
              <IconSvg
                rounded
                bgColor={isActive ? 'success_new' : 'inactive'}
                fillColor={isActive ? 'success_new' : 'inactive'}
                height="auto"
                svgComponent={<IntegrationTableIcon />}
                width="40px"
                withPadding="small"
              />
              <div className="flex-1">
                <Tooltip
                  isClickable
                  noArrow
                  alignment="start"
                  content={info.getValue()}
                  position="top"
                >
                  <Text fontWeight="medium" variant="text">
                    {info.getValue()}
                  </Text>
                </Tooltip>
              </div>
            </div>
          );
        },
        meta: {
          widthClassName: 'w-[30%]',
          enableOrderColumn: true,
        },
      }),
      columnHelper.accessor('creationDate', {
        header: t('useRulesTableColumns.headerTable.createDate') as unknown as string,
        meta: {
          widthClassName: 'w-[20%]',
          enableOrderColumn: true,
        },
        cell: (info) => {
          const formattedDate = info.getValue()
            ? formatServeStringDateWithUserTimezone(info.getValue(), userData?.timeOffset ?? 0)
            : '-';
          return formattedDate;
        },
      }),
      columnHelper.accessor('lastExecutionDate', {
        header: t('useRulesTableColumns.headerTable.lastExecution') as unknown as string,
        meta: {
          widthClassName: 'w-[20%]',
        },
        cell: (info) => {
          const formattedDate = info.getValue()
            ? formatServeStringDateWithUserTimezone(
                info.getValue() ?? '',
                userData?.timeOffset ?? 0
              )
            : '-';
          return formattedDate;
        },
      }),
      columnHelper.accessor('totalExecutions', {
        header: t('useRulesTableColumns.headerTable.execution') as unknown as string,
        meta: {
          widthClassName: 'w-[10%]',
        },
        cell: (info) => {
          return info.getValue();
        },
      }),
      columnHelper.accessor('status', {
        header: t('useRulesTableColumns.headerTable.status') as unknown as string,
        meta: {
          widthClassName: 'w-[10%]',
        },
        cell: (info) => {
          const status = info.getValue();
          return (
            <Switch
              checked={status === EJourneyMessageState.ACTIVE}
              disabled={status === EJourneyMessageState.DRAFT}
              id={`switch-${info.row.id}`}
              onChange={handleSwitchChange(Number(info.row.original.journeyId))}
            />
          );
        },
      }),
      columnHelper.display({
        id: 'action-buttons',
        header: '',
        meta: {
          widthClassName: 'w-[10%]',
        },
        cell: (info) => {
          return (
            <div className="flex items-center gap-1">
              <Button
                gray
                onlyIcon
                outline
                id="log-actions"
                size="small"
                title={'preview action'}
                onClick={(event) => handleGoDetail(event, info.row.original)}
              >
                <IconSvg fillColor="gray-dark" svgComponent={<ClockIcon />} />
              </Button>
              <ActionRowMenu
                hideItems={['move']}
                id="options-actions-dropdown"
                onClick={handleRowMenuClick(info.row.original)}
              />
            </div>
          );
        },
      }),
    ];
  }, [
    columnHelper,
    t,
    userData?.timeOffset,
    handleSwitchChange,
    handleRowMenuClick,
    handleGoDetail,
  ]);

  return {
    columns,
    actionRowMenuData: actionRowMenuData ? [actionRowMenuData] : undefined,
  };
};
